import React from 'react';
import { Filter, Clock, Activity, Target, Brain, Heart } from 'lucide-react';

interface WorkoutFiltersProps {
  selectedLevel: string;
  onLevelChange: (level: string) => void;
  selectedDuration: number | null;
  onDurationChange: (duration: number | null) => void;
  selectedTarget: string;
  onTargetChange: (target: string) => void;
  selectedIntensity: string;
  onIntensityChange: (intensity: string) => void;
  selectedImpact: string;
  onImpactChange: (impact: string) => void;
}

export function WorkoutFilters({
  selectedLevel,
  onLevelChange,
  selectedDuration,
  onDurationChange,
  selectedTarget,
  onTargetChange,
  selectedIntensity,
  onIntensityChange,
  selectedImpact,
  onImpactChange
}: WorkoutFiltersProps) {
  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced', 'Elite'];
  const durations = [15, 30, 45, 60, 90];
  const targets = ['All', 'Upper Body', 'Lower Body', 'Core', 'Full Body', 'Cardio'];
  const intensities = ['All', 'Low', 'Moderate', 'High', 'Peak'];
  const impacts = ['All', 'Low', 'Medium', 'High'];

  return (
    <div className="theme-card p-4 space-y-6">
      <div className="flex items-center gap-2 text-primary">
        <Filter className="w-5 h-5" />
        <h3 className="font-medium">Filters</h3>
      </div>

      {/* Level Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Target className="w-4 h-4" />
          <span>Level</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {levels.map(level => (
            <button
              key={level}
              onClick={() => onLevelChange(level)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedLevel === level
                  ? 'theme-button'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Duration Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>Duration</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onDurationChange(null)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              selectedDuration === null
                ? 'theme-button'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            All
          </button>
          {durations.map(duration => (
            <button
              key={duration}
              onClick={() => onDurationChange(duration)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedDuration === duration
                  ? 'theme-button'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {duration}m
            </button>
          ))}
        </div>
      </div>

      {/* Target Area Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Brain className="w-4 h-4" />
          <span>Target Area</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {targets.map(target => (
            <button
              key={target}
              onClick={() => onTargetChange(target)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedTarget === target
                  ? 'theme-button'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {target}
            </button>
          ))}
        </div>
      </div>

      {/* Intensity Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="w-4 h-4" />
          <span>Intensity</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {intensities.map(intensity => (
            <button
              key={intensity}
              onClick={() => onIntensityChange(intensity)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedIntensity === intensity
                  ? 'theme-button'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {intensity}
            </button>
          ))}
        </div>
      </div>

      {/* Impact Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Heart className="w-4 h-4" />
          <span>Impact Level</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {impacts.map(impact => (
            <button
              key={impact}
              onClick={() => onImpactChange(impact)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedImpact === impact
                  ? 'theme-button'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {impact}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}